import type { CSSProperties } from 'react';
import { BaseApiService } from './BaseApiService';

export const STORAGE_LOCATIONS = ['Honeyware', 'Cool Room', 'Warehouse', 'Chocolate room'] as const;
export type StorageLocation = (typeof STORAGE_LOCATIONS)[number];

export type BloomStockItemKind = 'ingredient' | 'box' | 'container';
export type BulkStorageLocation = StorageLocation;
export type BloomStockStatusLevel = 'good' | 'low' | 'critical' | 'out_of_stock';

export function merge_vendor_options(
  server_options: string[],
  selected: string[],
  extra: string[]
): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const raw of [...server_options, ...selected, ...extra]) {
    const name = raw.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(name);
  }
  return merged.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function bloom_stock_status_label(status: BloomStockStatusLevel | undefined): string {
  switch (status) {
    case 'out_of_stock':
      return 'OUT OF STOCK';
    case 'critical':
      return 'CRITICAL';
    case 'low':
      return 'LOW';
    case 'good':
      return 'GOOD';
    default:
      return 'UNKNOWN';
  }
}

export function bloom_stock_status_from_item(item: {
  stock_status?: BloomStockStatusLevel;
  cases_on_hand: number;
  is_low: boolean;
}): BloomStockStatusLevel {
  if (item.stock_status) return item.stock_status;
  if (item.cases_on_hand <= 0) return 'out_of_stock';
  return item.is_low ? 'low' : 'good';
}

export function bloom_stock_status_style(status: BloomStockStatusLevel): CSSProperties {
  switch (status) {
    case 'out_of_stock':
    case 'critical':
      return { color: '#c53030', fontWeight: 600 };
    case 'low':
      return { color: '#c05621', fontWeight: 600 };
    case 'good':
      return { color: '#276749', fontWeight: 600 };
    default:
      return {};
  }
}

export interface BloomStockLot {
  id: number;
  bulk_product_id: number;
  received_date: string;
  expiry_date: string;
  storage_location: string;
  cases_on_hand: number;
  notes?: string | null;
}

export interface BulkProduct {
  id: number;
  name: string;
  sku_number: string;
  vendor?: string;
  vendors?: string[];
  description?: string | null;
  ideal_order_cases?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateBulkProductData {
  name: string;
  sku_number: string;
  vendor?: string;
  vendors?: string[];
}

export interface UpdateBulkProductData {
  name?: string;
  sku_number?: string;
  vendor?: string;
  vendors?: string[];
}

export interface BloomStockItem extends BulkProduct {
  vendors?: string[];
  is_bloom_stock: boolean;
  item_kind: BloomStockItemKind;
  low_stock_threshold_cases: number;
  description?: string | null;
  ideal_order_cases: number;
  cases_on_hand: number;
  is_low: boolean;
  stock_status?: BloomStockStatusLevel;
  expired_lot_cases?: number;
  expiring_soon_lot_cases?: number;
}

export interface ReceiveBloomStockLotData {
  bulk_product_id: number;
  cases: number;
  received_date: string;
  expiry_date: string;
  storage_location: BulkStorageLocation;
  reason?: string;
  notes?: string;
}

export interface CreateBloomStockData {
  name: string;
  sku_number: string;
  vendor?: string;
  vendors?: string[];
  item_kind: BloomStockItemKind;
  low_stock_threshold_cases?: number;
  description?: string | null;
  ideal_order_cases?: number;
  initial_cases?: number;
}

export interface UpdateBloomStockData {
  name?: string;
  sku_number?: string;
  vendor?: string;
  vendors?: string[];
  item_kind?: BloomStockItemKind;
  low_stock_threshold_cases?: number;
  description?: string | null;
  ideal_order_cases?: number;
}

export interface BloomStockAdjustmentResult {
  item: BloomStockItem;
  cases_before: number;
  cases_after: number;
  cases_adjusted: number;
  became_low: boolean;
  is_low: boolean;
}

export interface BloomStockImportResult {
  applied: BloomStockAdjustmentResult[];
  errors: { row: number; sku_number?: string; message: string }[];
}

export interface Pagination {
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

const empty_pagination: Pagination = { total: 0, totalPages: 0, currentPage: 1, pageSize: 20 };

class BulkProductService extends BaseApiService {
  async getAllBulkProducts(): Promise<BulkProduct[]> {
    const response = await this.get<{ success: boolean; data: BulkProduct[] }>('/bulk-products');
    return response.data || [];
  }

  async getVendorNames(): Promise<string[]> {
    const response = await this.get<{ success: boolean; data: string[] }>(
      '/bulk-products/vendor-names'
    );
    return response.data || [];
  }

  async getBulkProductsPaginated(
    page = 1,
    pageSize = 20,
    search?: string
  ): Promise<{
    bulkProducts: BulkProduct[];
    pagination: Pagination;
  }> {
    const params: Record<string, string> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
    };
    if (search) params.search = search;
    const response = await this.get<{
      success: boolean;
      data: BulkProduct[];
      pagination: Pagination;
    }>('/bulk-products', params);

    return {
      bulkProducts: response.data || [],
      pagination: response.pagination || empty_pagination,
    };
  }

  async searchBulkProducts(searchTerm: string): Promise<BulkProduct[]> {
    const params = { search: searchTerm };
    const response = await this.get<{ success: boolean; data: BulkProduct[] }>(
      '/bulk-products',
      params
    );
    return response.data || [];
  }

  async getBulkProductById(id: number): Promise<BulkProduct> {
    const response = await this.get<{ success: boolean; data: BulkProduct }>(
      `/bulk-products/${id}`
    );
    if (!response.data) {
      throw new Error('Bulk product not found');
    }
    return response.data;
  }

  async createBulkProduct(data: CreateBulkProductData): Promise<BulkProduct> {
    const response = await this.post<{ success: boolean; data: BulkProduct }>(
      '/bulk-products',
      data
    );
    if (!response.data) {
      throw new Error('Failed to create bulk product');
    }
    return response.data;
  }

  async updateBulkProduct(id: number, data: UpdateBulkProductData): Promise<BulkProduct> {
    const response = await this.put<{ success: boolean; data: BulkProduct }>(
      `/bulk-products/${id}`,
      data
    );
    if (!response.data) {
      throw new Error('Failed to update bulk product');
    }
    return response.data;
  }

  async deleteBulkProduct(id: number): Promise<string> {
    const response = await this.delete<{ success: boolean; message: string }>(
      `/bulk-products/${id}`
    );
    return response.message || 'Bulk product deleted successfully';
  }

  async searchPackagingOptions(
    kind: 'box' | 'container',
    search: string,
    page = 1,
    pageSize = 20
  ): Promise<{
    items: Array<{ id: number; name: string; sku_number: string; vendor?: string | null }>;
    pagination: Pagination;
  }> {
    const response = await this.get<{
      success: boolean;
      data: Array<{ id: number; name: string; sku_number: string; vendor?: string | null }>;
      pagination: Pagination;
    }>('/bulk-products/packaging-options', {
      kind,
      search,
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    return {
      items: response.data || [],
      pagination: response.pagination || empty_pagination,
    };
  }

  async getPackagingOption(
    id: number,
    kind: 'box' | 'container' = 'container'
  ): Promise<{ id: number; name: string; sku_number: string; vendor?: string | null } | null> {
    const response = await this.get<{
      success: boolean;
      data: { id: number; name: string; sku_number: string; vendor?: string | null } | null;
    }>('/bulk-products/packaging-options', { kind, include_id: id.toString() });
    return response.data || null;
  }

  async getBloomStockPaginated(
    page = 1,
    pageSize = 20,
    search?: string,
    filter?: 'all' | 'low'
  ): Promise<{
    items: BloomStockItem[];
    pagination: Pagination;
  }> {
    const params: Record<string, string> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
    };
    if (search) params.search = search;
    if (filter && filter !== 'all') params.filter = filter;

    const response = await this.get<{
      success: boolean;
      data: BloomStockItem[];
      pagination: Pagination;
    }>('/bulk-products/stock', params);

    return {
      items: response.data || [],
      pagination: response.pagination || empty_pagination,
    };
  }

  async getBloomStockSummary(): Promise<{ total_count: number; low_count: number }> {
    const response = await this.get<{
      success: boolean;
      data: { total_count: number; low_count: number };
    }>('/bulk-products/stock/summary');
    return response.data || { total_count: 0, low_count: 0 };
  }

  async getBloomStockLots(bulk_product_id: number): Promise<BloomStockLot[]> {
    const response = await this.get<{ success: boolean; data: BloomStockLot[] }>(
      `/bulk-products/stock/${bulk_product_id}/lots`
    );
    return response.data || [];
  }

  async createBloomStock(data: CreateBloomStockData): Promise<BloomStockItem> {
    const response = await this.post<{ success: boolean; data: BloomStockItem }>(
      '/bulk-products/stock',
      data
    );
    if (!response.data) {
      throw new Error('Failed to create Bloom stock item');
    }
    return response.data;
  }

  async updateBloomStock(id: number, data: UpdateBloomStockData): Promise<BloomStockItem> {
    const response = await this.put<{ success: boolean; data: BloomStockItem }>(
      `/bulk-products/stock/${id}`,
      data
    );
    if (!response.data) {
      throw new Error('Failed to update Bloom stock item');
    }
    return response.data;
  }

  async adjustBloomStock(
    id: number,
    cases_adjusted: number,
    reason?: string
  ): Promise<BloomStockAdjustmentResult> {
    const response = await this.post<{ success: boolean; data: BloomStockAdjustmentResult }>(
      `/bulk-products/stock/${id}/adjust`,
      { cases_adjusted, reason }
    );
    if (!response.data) {
      throw new Error('Failed to adjust Bloom stock');
    }
    return response.data;
  }

  async getBloomStockByVendor(
    vendor: string,
    exclude_id?: number
  ): Promise<BloomStockItem[]> {
    const params: Record<string, string> = { vendor };
    if (exclude_id != null) params.exclude_id = String(exclude_id);
    const response = await this.get<{ success: boolean; data: BloomStockItem[] }>(
      '/bulk-products/stock/by-vendor',
      params
    );
    return response.data || [];
  }

  async receiveBloomStockLot(data: ReceiveBloomStockLotData): Promise<BloomStockAdjustmentResult> {
    const response = await this.post<{ success: boolean; data: BloomStockAdjustmentResult }>(
      '/bulk-products/stock/receive-lot',
      data
    );
    if (!response.data) {
      throw new Error('Failed to receive Bloom stock lot');
    }
    return response.data;
  }

  async importBloomStock(file_base64: string, file_name: string): Promise<BloomStockImportResult> {
    const response = await this.post<{ success: boolean; data: BloomStockImportResult }>(
      '/bulk-products/stock/import',
      { file_base64, file_name }
    );
    if (!response.data) {
      throw new Error('Failed to import Bloom stock');
    }
    return response.data;
  }
}

export const bulkProductService = new BulkProductService();
